import { backend_api } from '@/utils/api'
import bus from '@/utils/event_bus'
import { defineStore } from 'pinia'
import { useAppStore } from "@/stores/app"
import { useRefurbishmentStore } from "@/stores/refurb"
import { useShipmentsStore } from "@/stores/shipments"
import { usePartnumbersStore } from "@/stores/partnumbers"
import { useExternalShipmentsStore } from "@/stores/external_shipments"
import { useContainersStore } from "@/stores/containers"
import base from "@/stores/base"

function determineUserLanguage(auth) {
    if (auth.multilang && auth.language) {
        return auth.language || "en"
    } else
        return auth.company.meta.language || "en"
}


export const useSessionStore = defineStore('session', {
    state: () => ({
        access_token: localStorage.getItem('access_token'),
        refresh_token: localStorage.getItem('refresh_token'),
        authenticated: !!localStorage.getItem('access_token'),
        user_object: {
            _flag: "not-initialized",
            username: "",
            auth: {}
        },

        // THIS COMES FROM THE SIM_AUTH DJANGO APP
        companies: [],
        users: [],
        holdings: [],

        // Issue #145: compañías activas en el selector global (UUIDs).
        // Se reinicia a la compañía propia en cada carga (sin persistencia).
        active_companies: [],
    }),
    getters: {
        iam_admin: state => state.user_object.auth?.company_admin,
        has_admin_privileges: state => state.user_object.auth?.company_admin || state.user_object.auth?.is_superuser || state.user_object.auth?.as_admin,
        my_company: state => state.user_object.auth?.company,
        my_holding: state => state.user_object.auth?.company?.holding,
        companies_in_my_holding: state => {
            if (!state.user_object.auth?.company?.holding) return [];
            const holdingId = state.user_object.auth.company.holding.uuid;
            return state.companies.filter(company =>
                company.holding && company.holding.uuid === holdingId
            );
        },
        is_external_customer: state => state.user_object.auth?.is_external_customer,
        is_external_workshop: state => state.user_object.auth?.is_external_workshop,

        // Issue #145: compañías que el usuario PUEDE seleccionar (propia + holding), deduplicadas.
        selectable_companies() {
            const own = this.my_company;
            if (!own) return [];
            const map = new Map();
            map.set(own.uuid, own);
            for (const c of this.companies_in_my_holding) {
                if (c?.uuid) map.set(c.uuid, c);
            }
            return Array.from(map.values());
        },
        // Valor de la cabecera X-Selected-Companies.
        // Si la selección está vacía (p. ej. init aún no ha corrido), cae a la
        // compañía propia para que el backend SIEMPRE haga scope a own por defecto.
        selected_companies_header() {
            const list = this.active_companies.length
                ? this.active_companies
                : (this.my_company?.uuid ? [this.my_company.uuid] : [])
            return list.join(',')
        },
        // Mostrar el selector a clientes externos que tienen un holding por encima.
        // Se basa en el user object (disponible al instante), no en la lista de
        // compañías (que carga async) -> la barra aparece sin esperas.
        show_company_selector() {
            return !!this.is_external_customer && !!this.my_holding;
        },
    },
    actions: {
        setTokens(data) {
            this.access_token = data.access
            this.refresh_token = data.refresh
            this.authenticated = true
            localStorage.setItem('access_token', this.access_token)
            localStorage.setItem('refresh_token', this.refresh_token)
        },
        refreshToken(at) {
            this.access_token = at
            localStorage.setItem('access_token', this.access_token)
        },
        cleanSession() {
            this.access_token = ""
            this.refresh_token = ""
            this.authenticated = false
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            console.log("[Auth] Session cleaned from localStorage")
        },
        resetStore() {
            // Limpiar datos de usuario
            this.user_object = {
                _flag: "not-initialized",
                username: "",
                auth: {}
            }
            // Limpiar datos de compañías, usuarios y holdings
            this.companies = []
            this.users = []
            this.holdings = []
            this.active_companies = []
            console.log("[Auth] Store data reset")
        },
        
        async logout() {
            console.log("[Auth] Starting logout process")
            
            try {
                // Try to logout from backend first
                await backend_api.post('/auth/logout/')
                console.log("[Auth] Backend logout successful")
            } catch (error) {
                console.warn("[Auth] Backend logout failed, proceeding with local cleanup:", error.message)
                
                // If backend logout fails due to CSRF/CORS issues, continue with local cleanup
                if (error.response?.status === 403) {
                    console.log("[Auth] CSRF/CORS error during logout, cleaning session locally")
                }
            }
            
            // Always clean local session regardless of backend response
            this.cleanSession()
            this.resetStore()
            
            // Clear any app-level state
            const app_store = useAppStore()
            app_store.resetStore()
            
            console.log("[Auth] Logout process completed")
            
            // Emit success notification
            bus.emit("notification", {
                timeout: 3000,
                text: "Logged out successfully",
                color: "info"
            })
            
            return Promise.resolve()
        },
        setUserObject(data) {
            this.user_object = data
            // Issue #145: en cuanto hay user object, dejar la selección global apuntando
            // a la compañía propia (si es cliente externo y aún está vacía). Garantiza que
            // el selector arranca filtrando por own y que su UI lo refleja sin esperar a la
            // carga async de compañías.
            if (this.is_external_customer && this.active_companies.length === 0) {
                this.initActiveCompanies()
            }
        },

        // Issue #145: selector global de compañías.
        // Inicializa la selección a la compañía propia (default del issue, sin persistencia).
        initActiveCompanies() {
            const own = this.my_company
            this.active_companies = own?.uuid ? [own.uuid] : []
        },
        // Aplica una nueva selección (garantizando >=1: si se vacía, vuelve a la propia)
        // y notifica a las vistas para que recarguen sus datos.
        setActiveCompanies(uuids) {
            const list = (uuids || []).filter(Boolean)
            if (list.length === 0) {
                this.initActiveCompanies()
            } else {
                this.active_companies = list
            }
            bus.emit('companies_changed')
        },

        login(data) {
            const app_store = useAppStore()
            // Reset all stores before loading new user data
            this.resetStore()
            app_store.resetStore()
            useRefurbishmentStore().resetStore()
            useShipmentsStore().resetStore()
            usePartnumbersStore().resetStore()
            useExternalShipmentsStore().resetStore()
            useContainersStore().resetStore()
            return backend_api.post('/my_user/token/', data)
                .then(({ data }) => {
                    this.setTokens(data)
                    app_store.refreshBaseUserData(true)
                    app_store.initialLoad()
                })
        },
        refresh() {
            const app_store = useAppStore()
            console.log("[i] Refreshing access token")
            if (this.refresh_token == "") bus.emit("events/session/logout")
            return backend_api.post('/my_user/token/refresh/', { refresh: this.refresh_token })
                .then(({ data }) => {
                    this.refreshToken(data.access)
                    app_store.refreshBaseUserData(true)
                })
                .catch(() => {
                    bus.emit("notification", {
                        timeout: 8000,
                        text: "Failed to refresh session. You have to login again.",
                        color: "error"
                    });
                })
        },
        getUserObject() {
            return backend_api.get('/my_user/')
                .then(({ data }) => {
                    this.setUserObject(data)
                })
                .catch(e => console.log(`Cant get User Object: ${e}`))
        },


        // Sync mutations
        setCompanies: base.actions.setter("companies"),
        createCompany: base.actions.creator("companies"),
        updateCompany: base.actions.updater("companies"),
        removeCompany: base.actions.remover("companies"),
        // Async backend actions
        loadCompanies: base.actions.loader("companies/", "Companies"),
        postCompany: base.actions.poster("companies/", "Company"),
        putCompany: base.actions.putter("companies/", "Company"),
        deleteCompany: base.actions.deleter("companies/", "Company"),

        // Sync mutations
        setUsers: base.actions.setter("users"),
        createUser: base.actions.creator("users"),
        updateUser: base.actions.updater("users"),
        removeUser: base.actions.remover("users"),
        // Async backend actions
        loadUsers: base.actions.loader("users/", "Users"),
        postUser: base.actions.poster("users/", "User"),
        putUser: base.actions.putter("users/", "User"),
        deleteUser: base.actions.deleter("users/", "User"),
        
        // Sync mutations for holdings
        setHoldings: base.actions.setter("holdings"),
        createHolding: base.actions.creator("holdings"),
        updateHolding: base.actions.updater("holdings"),
        removeHolding: base.actions.remover("holdings"),
        // Async backend actions for holdings
        loadHoldings: base.actions.loader("holdings/", "Holdings"),
        postHolding: base.actions.poster("holdings/", "Holding"),
        putHolding: base.actions.putter("holdings/", "Holding"),
        deleteHolding: base.actions.deleter("holdings/", "Holding"),
        postUserPassword(payload) {
            return backend_api.put('users/' + payload.id + "/change_password/", payload)
                .then(({ data }) =>
                    bus.emit("notification", {
                        timeout: 3000,
                        text: data.detail,
                        color: "success",
                    })
                )
                .catch(e => bus.emit("notification", {
                    timeout: 8000,
                    text: e,
                    color: "error",
                }))
        },
    },
})
